/* ============================================================================
   EQUIPOS Y EMPRESAS — de quién era cada equipo, y desde cuándo

   El dominio ya sabía contestar «¿de quién era este equipo en marzo?»
   (`empresaDelEquipo`), pero no había ninguna pantalla que lo preguntara. La
   respuesta existía solo en una prueba.

   ═══ LO QUE SE MUESTRA, Y LO QUE NO ═══

   Se muestran DOS cosas distintas, a propósito:

     · el dueño de HOY, que es el que decide quién opera y a quién se le carga
       el mantenimiento;
     · el HISTORIAL con fecha, que es el que permite explicar un vuelo de hace
       seis meses sin reescribir la historia.

   No hay una tercera: el dueño actual no se guarda aparte. Se calcula con la
   última asignación que ya empezó, y por eso no puede quedar distinto del
   historial.

   ═══ QUIÉN PUEDE ASIGNAR ═══

   Leer el historial es parte de la auditoría: lo ve cualquiera que pueda leer
   vuelos. Asignar cambia de quién es el equipo, y eso pide `empresa.escribir`.
   La pantalla no dibuja el formulario si el perfil no lo tiene, y el guardado
   vuelve a comprobarlo: un botón es una invitación, no un permiso.
   ========================================================================== */

import { useMemo, useState } from "react";

import { puede } from "../../domain/identidad.js";
import { asignarEquipo, empresaDelEquipo } from "../../domain/estadisticas.js";
import "../administracion/administracion.css";

const hoy = () => new Date().toISOString().slice(0, 10);

function nombreDeAeronave(aeronaves, id) {
  const a = (Array.isArray(aeronaves) ? aeronaves : []).find((x) => x && x.id === id);
  return a?.matricula || a?.nombre || id || "(sin aeronave)";
}

function nombreDeEmpresa(empresas, id) {
  const e = (Array.isArray(empresas) ? empresas : []).find((x) => x && x.id === id);
  return e?.nombre || id || "(sin empresa)";
}

export default function PanelAsignaciones({
  aeronaves = [],
  empresas = [],
  asignaciones = [],
  alCambiarAsignaciones = null,
  perfil = null,
  usuario = null,
}) {
  const puedeAsignar = puede(perfil, "empresa.escribir");

  const [aeronaveId, setAeronaveId] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [desde, setDesde] = useState(hoy);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const lista = useMemo(
    () => (Array.isArray(asignaciones) ? asignaciones.filter(Boolean) : []),
    [asignaciones],
  );

  const historial = useMemo(
    () => [...lista].sort((a, b) => String(b.desde).localeCompare(String(a.desde))),
    [lista],
  );

  const asignar = (evento) => {
    evento.preventDefault();
    setError("");
    setAviso("");

    if (!puedeAsignar) {
      setError("Tu perfil puede consultar el historial, pero no asignar equipos.");
      return;
    }
    if (!aeronaveId) { setError("Elige la aeronave."); return; }
    if (!empresaId) { setError("Elige la empresa."); return; }
    if (!desde) { setError("Indica desde qué fecha rige la asignación."); return; }

    const repetida = lista.some(
      (a) => a.aeronaveId === aeronaveId && a.empresaId === empresaId && a.desde === desde,
    );
    if (repetida) {
      setError("Esa asignación ya está registrada con la misma fecha.");
      return;
    }

    try {
      const siguiente = asignarEquipo(lista, {
        aeronaveId,
        empresaId,
        desde,
        quien: usuario?.nombre || usuario?.id || null,
      });
      alCambiarAsignaciones?.(siguiente);
      setAviso(
        `${nombreDeAeronave(aeronaves, aeronaveId)} queda con ${nombreDeEmpresa(empresas, empresaId)} desde el ${desde}.`,
      );
      setEmpresaId("");
      setDesde(hoy());
    } catch (e) {
      setError(e?.message || "No se pudo registrar la asignación.");
    }
  };

  const sinAeronaves = !Array.isArray(aeronaves) || aeronaves.length === 0;
  const sinEmpresas = !Array.isArray(empresas) || empresas.length === 0;

  return (
    <section className="adm">
      <header className="adm__entrada">
        <div className="adm__entrada-texto">
          <h2 className="adm__titulo">Equipos y empresas</h2>
          <p className="adm__nota">
            El dueño de hoy y el historial con fecha. El dueño actual no se guarda aparte:
            sale de la última asignación que ya empezó, así no puede contradecir al historial.
          </p>
        </div>
      </header>

      {error && <p className="adm__mensaje adm__mensaje--problema" role="alert">{error}</p>}
      {aviso && <p className="adm__mensaje adm__mensaje--ok" role="status">{aviso}</p>}

      {puedeAsignar ? (
        <form className="adm__alta" onSubmit={asignar}>
          <h3 className="adm__alta-titulo">Asignar un equipo</h3>

          <label className="adm__campo">
            <span>Aeronave</span>
            <select value={aeronaveId} onChange={(e) => setAeronaveId(e.target.value)} disabled={sinAeronaves}>
              <option value="">{sinAeronaves ? "No hay aeronaves cargadas" : "Elige una"}</option>
              {aeronaves.map((a) => (
                <option key={a.id} value={a.id}>{a.matricula || a.nombre || a.id}</option>
              ))}
            </select>
          </label>

          <label className="adm__campo">
            <span>Empresa</span>
            <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)} disabled={sinEmpresas}>
              <option value="">{sinEmpresas ? "No hay empresas cargadas" : "Elige una"}</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nombre || e.id}</option>
              ))}
            </select>
          </label>

          <label className="adm__campo">
            <span>Desde</span>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>

          <button type="submit" className="adm__boton">Asignar</button>

          {sinAeronaves && (
            <p className="adm__nota">
              Para asignar hace falta una aeronave en la flota. Se carga en la vista de operación.
            </p>
          )}
          {sinEmpresas && (
            <p className="adm__nota">
              Para asignar hace falta una empresa. Se crea en «Empresa y usuarios».
            </p>
          )}
        </form>
      ) : (
        <p className="adm__bloqueo" role="status">
          Solo lectura: tu perfil puede consultar el historial, pero no asignar equipos.
          Asignar pide el permiso de editar los datos de la empresa.
        </p>
      )}

      <div className="adm__seccion">
        <h3 className="adm__subtitulo">Dueño de hoy</h3>
        {sinAeronaves ? (
          <p className="adm__nota">No hay aeronaves cargadas.</p>
        ) : (
          <div className="adm__tabla-caja">
            <table className="adm__tabla">
              <caption className="adm__caption">La última asignación que ya empezó, por aeronave.</caption>
              <thead>
                <tr>
                  <th scope="col">Aeronave</th>
                  <th scope="col">Empresa</th>
                  <th scope="col">Desde</th>
                </tr>
              </thead>
              <tbody>
                {aeronaves.map((a) => {
                  const empresa = empresaDelEquipo(lista, a.id, hoy());
                  const asignacion = [...lista]
                    .filter((x) => x.aeronaveId === a.id && String(x.desde) <= hoy())
                    .sort((x, y) => String(y.desde).localeCompare(String(x.desde)))[0];
                  return (
                    <tr key={a.id}>
                      <th scope="row">{a.matricula || a.nombre || a.id}</th>
                      <td>{empresa ? nombreDeEmpresa(empresas, empresa) : "Sin empresa asignada"}</td>
                      <td>{asignacion?.desde || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="adm__seccion">
        <h3 className="adm__subtitulo">Historial</h3>
        {historial.length === 0 ? (
          <p className="adm__nota">Todavía no hay asignaciones registradas.</p>
        ) : (
          <div className="adm__tabla-caja">
            <table className="adm__tabla">
              <caption className="adm__caption">Todas las asignaciones, de la más reciente a la más vieja.</caption>
              <thead>
                <tr>
                  <th scope="col">Desde</th>
                  <th scope="col">Aeronave</th>
                  <th scope="col">Empresa</th>
                  <th scope="col">Quién la registró</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((a, i) => (
                  <tr key={`${a.aeronaveId}-${a.empresaId}-${a.desde}-${i}`}>
                    <th scope="row">{a.desde}</th>
                    <td>{nombreDeAeronave(aeronaves, a.aeronaveId)}</td>
                    <td>{nombreDeEmpresa(empresas, a.empresaId)}</td>
                    <td>{a.quien || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
